import { makeAutoObservable, runInAction } from 'mobx';

import {
  createCostAlertRule,
  getCostAlertRule,
  listCostAlertRules,
  updateCostAlertRule,
  deleteCostAlertRule,
  enableCostAlertRule,
  disableCostAlertRule,
  testCostAlertRule,
  getCostAlertRuleWithOverrides,
  setServiceThresholdOverride,
  getServiceThresholdOverrides,
  removeServiceThresholdOverride,
  removeAllServiceThresholdOverrides
} from '@/api';

class CostAlertRule {
  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  // State
  rules = [];
  rulesWithOverrides = []; // Store rules with their service overrides
  rule = null;
  ruleWithOverrides = null;
  serviceOverrides = [];
  testResult = null;
  loading = false;

  // Clear states
  clearRules() {
    this.rules = [];
  }

  clearRulesWithOverrides() {
    this.rulesWithOverrides = [];
  }

  clearRule() {
    this.rule = null;
  }

  clearRuleWithOverrides() {
    this.ruleWithOverrides = null;
  }

  clearServiceOverrides() {
    this.serviceOverrides = [];
  }

  clearTestResult() {
    this.testResult = null;
  }

  // Get all rules
  async getRules(params) {
    this.loading = true;
    let res;
    try {
      res = await listCostAlertRules(params);
      if (res) {
        runInAction(() => {
          this.rules = res;
        });
      }
    } catch (err) {
      console.error('Failed to fetch cost alert rules:', err);
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
    return res;
  }

  // Get all rules with service overrides (for table display)
  async getRulesWithOverrides(params) {
    this.loading = true;
    let res;
    try {
      // First get all rules
      const rules = await listCostAlertRules(params);
      if (!rules) {
        return null;
      }

      // Then fetch overrides for each rule in parallel
      const rulesWithOverrides = await Promise.all(
        rules.map(async (rule) => {
          try {
            const ruleWithOverrides = await getCostAlertRuleWithOverrides(rule.id);
            return ruleWithOverrides || { ...rule, serviceOverrides: [] };
          } catch (err) {
            console.error(`Failed to fetch overrides for rule ${rule.id}:`, err);
            return { ...rule, serviceOverrides: [] };
          }
        })
      );

      runInAction(() => {
        this.rules = rules;
        this.rulesWithOverrides = rulesWithOverrides;
      });

      res = rulesWithOverrides;
    } catch (err) {
      console.error('Failed to fetch cost alert rules with overrides:', err);
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
    return res;
  }

  // Get single rule
  async getRule(id) {
    this.loading = true;
    let res;
    try {
      res = await getCostAlertRule(id);
      if (res) {
        runInAction(() => {
          this.rule = res;
        });
      }
    } catch (err) {
      console.error('Failed to fetch cost alert rule:', err);
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
    return res;
  }

  // Create rule
  async createRule(params) {
    let res;
    try {
      res = await createCostAlertRule(params);
      if (res) {
        runInAction(() => {
          this.rules.push(res);
        });
      }
    } catch (err) {
      console.error('Failed to create cost alert rule:', err);
      throw err;
    }
    return res;
  }

  // Update rule
  async updateRule(id, params) {
    let res;
    try {
      res = await updateCostAlertRule(id, params);
      if (res) {
        runInAction(() => {
          const index = this.rules.findIndex(r => r.id === id);
          if (index !== -1) {
            this.rules[index] = res;
          }
        });
      }
    } catch (err) {
      console.error('Failed to update cost alert rule:', err);
      throw err;
    }
    return res;
  }

  // Delete rule
  async deleteRule(id) {
    let res;
    try {
      res = await deleteCostAlertRule(id);
      if (res !== undefined) {
        runInAction(() => {
          this.rules = this.rules.filter(r => r.id !== id);
        });
      }
    } catch (err) {
      console.error('Failed to delete cost alert rule:', err);
      throw err;
    }
    return res;
  }

  // Enable rule
  async enableRule(id) {
    let res;
    try {
      res = await enableCostAlertRule(id);
      if (res) {
        runInAction(() => {
          const index = this.rules.findIndex(r => r.id === id);
          if (index !== -1) {
            this.rules[index] = res;
          }
        });
      }
    } catch (err) {
      console.error('Failed to enable cost alert rule:', err);
      throw err;
    }
    return res;
  }

  // Disable rule
  async disableRule(id) {
    let res;
    try {
      res = await disableCostAlertRule(id);
      if (res) {
        runInAction(() => {
          const index = this.rules.findIndex(r => r.id === id);
          if (index !== -1) {
            this.rules[index] = res;
          }
        });
      }
    } catch (err) {
      console.error('Failed to disable cost alert rule:', err);
      throw err;
    }
    return res;
  }

  // Test rule
  async testRule(ruleId, params) {
    this.loading = true;
    let res;
    try {
      res = await testCostAlertRule(ruleId, params);
      console.log('Test API response:', res);
      runInAction(() => {
        // Set testResult even if res is null/empty to show appropriate message
        this.testResult = res || { noData: true };
      });
    } catch (err) {
      console.error('Failed to test cost alert rule:', err);
      throw err;
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
    return res;
  }

  // Get rule with service overrides
  async getRuleWithOverrides(id) {
    this.loading = true;
    let res;
    try {
      res = await getCostAlertRuleWithOverrides(id);
      if (res) {
        runInAction(() => {
          this.ruleWithOverrides = res;
          this.serviceOverrides = res.serviceOverrides || [];
        });
      }
    } catch (err) {
      console.error('Failed to fetch rule with overrides:', err);
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
    return res;
  }

  // Get service overrides
  async getServiceOverrides(ruleId) {
    this.loading = true;
    let res;
    try {
      res = await getServiceThresholdOverrides(ruleId);
      if (res) {
        runInAction(() => {
          this.serviceOverrides = res;
        });
      }
    } catch (err) {
      console.error('Failed to fetch service overrides:', err);
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
    return res;
  }

  // Set service override
  async setServiceOverride(ruleId, params) {
    let res;
    try {
      res = await setServiceThresholdOverride(ruleId, params);
      if (res) {
        runInAction(() => {
          // Check if override already exists
          const index = this.serviceOverrides.findIndex(o => o.serviceCode === params.serviceCode);
          if (index !== -1) {
            this.serviceOverrides[index] = res;
          } else {
            this.serviceOverrides.push(res);
          }
        });
      }
    } catch (err) {
      console.error('Failed to set service override:', err);
      throw err;
    }
    return res;
  }

  // Remove service override
  async removeServiceOverride(ruleId, serviceCode) {
    let res;
    try {
      res = await removeServiceThresholdOverride(ruleId, serviceCode);
      if (res !== undefined) {
        runInAction(() => {
          this.serviceOverrides = this.serviceOverrides.filter(o => o.serviceCode !== serviceCode);
        });
      }
    } catch (err) {
      console.error('Failed to remove service override:', err);
      throw err;
    }
    return res;
  }

  // Remove all service overrides
  async removeAllServiceOverrides(ruleId) {
    let res;
    try {
      res = await removeAllServiceOverrides(ruleId);
      if (res !== undefined) {
        runInAction(() => {
          this.serviceOverrides = [];
        });
      }
    } catch (err) {
      console.error('Failed to remove all service overrides:', err);
      throw err;
    }
    return res;
  }
}

const costAlertRuleStore = new CostAlertRule();
export default costAlertRuleStore;
